import React, { useMemo, useLayoutEffect, useState, useCallback, useRef } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, RowAction } from "@/components/NewTable";
import { usePositions } from "../PositionsContext";
import { getAssignedUsersColumns, ShiftUserRow } from "../columns";
import { useToolTip } from "@/hooks/useToolTip";
import { useModal } from "@/hooks/useModal";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";
import { shiftsService } from "@/services/shiftService";
import { ShiftUserStatusType } from "@/types/db/shift_users";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";

type RootStackParamList = {
  Positions: undefined;
  AddUsers: { shiftPositionId?: string };
  AssignedUsers: { shiftPositionId?: string };
};

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  disabled?: boolean;
}

function HeaderButton({ icon, tooltip, onPress, disabled }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }} numberOfLines={1}>
          {tooltip}
        </ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x - 60, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    if (disabled) return;
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip, disabled]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4, opacity: disabled ? 0.4 : 1 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <MaterialCommunityIcons name={icon} size={24} color={disabled ? colors.textSecondary : colors.primary} />
    </Pressable>
  );
}

export default function AssignedUsersScreen() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const { shift_users: shiftUsersPermissions } = usePermissions();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "AssignedUsers">>();
  const { positions, updateUserInPosition, removeUserFromPosition } = usePositions();
  const { openModal, closeModal } = useModal();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const { navigateToEntity } = useNavigationGuard();
  const [searchValue, setSearchValue] = useState("");

  // Navigation handler for user names
  const handleUserPress = useCallback(
    (row: ShiftUserRow) => {
      // Save current entity to navigation history before navigating
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      // Navigate to user profile
      navigateToEntity("user", row.user_id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const shiftPositionId = route.params?.shiftPositionId;
  const position = positions.find((p) => p.shift_position_id === shiftPositionId);

  // Permission check for invite all action
  const canUpdateShiftUsers = shiftUsersPermissions.update;

  const columns = useMemo(() => getAssignedUsersColumns(colors, handleUserPress), [colors, handleUserPress]);

  const handleEdit = useCallback(
    async (row: ShiftUserRow, key: string, newValue: string) => {
      if (!shiftPositionId) return;

      try {
        if (key === "status") {
          await shiftsService.updateShiftUserStatus(shiftPositionId, row.id, newValue as ShiftUserStatusType);
          updateUserInPosition(shiftPositionId, row.id, { status: newValue });
        }
      } catch (error) {
        console.error(`Failed to update ${key}:`, error);
      }
    },
    [shiftPositionId, updateUserInPosition],
  );

  const handleDeleteUser = useCallback(
    async (row: ShiftUserRow) => {
      if (!shiftPositionId) return;

      const userName =
        row.user?.preferred_name || row.user?.first_name
          ? `${row.user?.preferred_name || row.user?.first_name} ${row.user?.last_name || ""}`.trim()
          : "this user";

      const performDelete = async () => {
        await shiftsService.removeShiftUser(shiftPositionId, row.id);
        removeUserFromPosition(shiftPositionId, row.id);
      };

      // Show confirmation if status is not QUEUED
      if (row.status !== "QUEUED") {
        openModal(
          <ConfirmModal
            title="Remove User"
            message={`Are you sure you want to remove ${userName}? Their status is ${row.status}.`}
            confirmText="Remove"
            cancelText="Cancel"
            onConfirm={async () => {
              closeModal();
              await performDelete();
            }}
            onCancel={closeModal}
          />,
        );
      } else {
        await performDelete();
      }
    },
    [shiftPositionId, openModal, closeModal, removeUserFromPosition],
  );

  const rowActions: RowAction<ShiftUserRow>[] = useMemo(
    () => [
      {
        icon: "trash-can-outline",
        label: "Delete",
        color: colors.red.text,
        onPress: (row) => handleDeleteUser(row),
      },
    ],
    [colors.red.text, handleDeleteUser],
  );

  const tableData: ShiftUserRow[] = useMemo(() => {
    if (!position?.invited_users) return [];
    return position.invited_users.map((u) => ({
      id: u.id,
      user_id: u.user?.id || u.id,
      status: u.status,
      meta_data: u.meta_data,
      user: u.user,
      position_name: position.name,
    }));
  }, [position]);

  // Count queued users for invite all action
  const queuedUsers = useMemo(() => tableData.filter((u) => u.status === "QUEUED"), [tableData]);
  const queuedCount = queuedUsers.length;

  const handleNavigateToAddUsers = useCallback(() => {
    navigation.navigate("AddUsers", { shiftPositionId });
  }, [navigation, shiftPositionId]);

  const handleInviteAll = useCallback(() => {
    if (!shiftPositionId || queuedCount === 0) return;

    openModal(
      <ConfirmModal
        title="Invite All Queued Users"
        message={`You are about to invite ${queuedCount} user${queuedCount !== 1 ? "s" : ""}. They will receive notifications about this shift.`}
        confirmText="Invite All"
        cancelText="Cancel"
        onConfirm={async () => {
          closeModal();
          try {
            await shiftsService.updateShiftUserStatusBatch(shiftPositionId, "QUEUED", "INVITED");
            // Update context for all queued users
            queuedUsers.forEach((user) => {
              updateUserInPosition(shiftPositionId, user.id, { status: "INVITED" });
            });
          } catch (error) {
            console.error("Failed to invite users:", error);
          }
        }}
        onCancel={closeModal}
      />,
    );
  }, [shiftPositionId, queuedCount, queuedUsers, openModal, closeModal, updateUserInPosition]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <View style={styles.headerTitleText}>
            <ThemedText style={styles.title}>{position?.name || "Assigned Users"}</ThemedText>
            <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
              {tableData.length} user{tableData.length !== 1 ? "s" : ""} assigned
            </ThemedText>
          </View>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
      headerRight: () => (
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <HeaderButton
            icon="send"
            tooltip={
              !canUpdateShiftUsers
                ? `You don't have permission to invite ${displayNames?.users?.plural?.toLowerCase() || "users"}`
                : queuedCount === 0
                  ? `No queued ${displayNames?.users?.plural?.toLowerCase() || "users"} to invite`
                  : `Invite ${queuedCount} queued ${queuedCount !== 1 ? displayNames?.users?.plural?.toLowerCase() || "users" : displayNames?.users?.name?.toLowerCase() || "user"}`
            }
            onPress={handleInviteAll}
            disabled={!canUpdateShiftUsers || queuedCount === 0}
          />
          <HeaderButton
            icon="plus"
            tooltip={`Add ${displayNames?.users?.name || "User"}`}
            onPress={handleNavigateToAddUsers}
          />
        </View>
      ),
    });
  }, [
    navigation,
    position?.name,
    tableData.length,
    colors,
    searchValue,
    handleNavigateToAddUsers,
    canUpdateShiftUsers,
    queuedCount,
    handleInviteAll,
    displayNames,
  ]);

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<ShiftUserRow>
        data={tableData}
        columns={columns}
        keyExtractor={(item) => item.id}
        searchValue={searchValue}
        sortable
        canEdit
        onEdit={handleEdit}
        defaultSort={{ key: "name", direction: "asc" }}
        emptyMessage="No users assigned to this position"
        emptyIcon="account-outline"
        rowActions={rowActions}
        minActionSlots={4}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitleText: {
    gap: 2,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 12,
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
});
